import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Iter244 · Sesión persistente: el backend fija una cookie httpOnly Secure
// SameSite=Lax en /auth/login. `withCredentials` hace que el navegador la
// envíe en cada request (misma-origen), sobreviviendo a F5 / deep-links
// aunque localStorage se pierda.
axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forcePasswordChange, setForcePasswordChange] = useState(false);

  // Iter213 · Auto-logout en 401 (token expirado o revocado)
  // Iter279 · Antes de redirigir (lo que descarta cualquier formulario sin
  // guardar en curso), avisa con un toast persistente y da unos segundos de
  // margen para que la persona pueda copiar lo que estaba escribiendo.
  useEffect(() => {
    let redirecting = false;
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        if (err?.response?.status === 401) {
          const detail = err?.response?.data?.detail || "";
          if (/expirad|revoc|invalidad/i.test(detail) && !redirecting) {
            redirecting = true;
            toast.error("Tu sesión ha caducado. Guarda lo que estabas escribiendo — vas a volver al login en unos segundos.", { duration: 6000 });
            setTimeout(() => {
              localStorage.removeItem("valoriza_token");
              delete axios.defaults.headers.common["Authorization"];
              if (!window.location.pathname.startsWith("/login")) {
                window.location.href = "/login?expired=1";
              }
            }, 4000);
          }
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("valoriza_token");
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    // Iter244 · Se intenta SIEMPRE (haya o no token en localStorage): la
    // cookie httpOnly puede seguir viva aunque localStorage esté vacío.
    axios.get(`${API}/auth/me`)
      .then(r => {
        setUser(r.data);
        if (r.data.force_password_change) setForcePasswordChange(true);
      })
      .catch(() => { localStorage.removeItem("valoriza_token"); delete axios.defaults.headers.common["Authorization"]; })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("valoriza_token", data.token);
    axios.defaults.headers.common["Authorization"] = `Bearer ${data.token}`;
    setUser(data.user);
    if (data.force_password_change) setForcePasswordChange(true);
    return data;
  };

  const logout = async () => {
    // Iter213 · Notifica al backend para revocar el jti server-side.
    try { await axios.post(`${API}/auth/logout`); } catch (_) { /* noop */ }
    localStorage.removeItem("valoriza_token");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
    setForcePasswordChange(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    await axios.put(`${API}/auth/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
    });
    setForcePasswordChange(false);
    // Refresh user to clear the flag
    const { data } = await axios.get(`${API}/auth/me`);
    setUser(data);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, forcePasswordChange, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
export { API };
